import React from "react";
import { Badge } from "../../ui";
import { FeatherAlertCircle } from "@subframe/core";

export interface SplitVendorSplitDetail {
  vendor_id?: string | null;
  vendor_name: string;
  amount: number;
  bills: Array<{
    bill_id?: string;
    bill_number?: string;
    total?: number;
    balance?: number;
    amount_applied?: number;
    adjustment_amount?: number;
    currency_symbol?: string;
    status?: string;
  }>;
  advance_payment: number;
}

export interface SplitVendorCategoryDetails {
  date?: string;
  reference_number?: string;
  split_count?: number;
  total_amount?: number;
  splits: SplitVendorSplitDetail[];
}

interface SplitVendorViewContentProps {
  details: SplitVendorCategoryDetails | null;
  isLoading: boolean;
  transactionCurrencySymbol?: string;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount: number, symbol = "₹"): string => {
  return `${symbol}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getSplitTotals = (details: SplitVendorCategoryDetails) => {
  return details.splits.reduce(
    (totals, split) => {
      const billTotals = (split.bills ?? []).reduce(
        (billSum, bill) => ({
          amountApplied:
            billSum.amountApplied + Number(bill.amount_applied ?? 0),
          adjustment: billSum.adjustment + Number(bill.adjustment_amount ?? 0),
        }),
        { amountApplied: 0, adjustment: 0 },
      );

      return {
        amountApplied: totals.amountApplied + billTotals.amountApplied,
        adjustment: totals.adjustment + billTotals.adjustment,
        advancePayment:
          totals.advancePayment + Number(split.advance_payment ?? 0),
      };
    },
    { amountApplied: 0, adjustment: 0, advancePayment: 0 },
  );
};

const SplitVendorViewContent: React.FC<SplitVendorViewContentProps> = ({
  details,
  isLoading,
  transactionCurrencySymbol = "₹",
}) => {
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12 text-body text-subtext-color">
        Loading split vendor details...
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
        <FeatherAlertCircle className="text-neutral-400" />
        <span>No split vendor details available for this transaction.</span>
      </div>
    );
  }

  const totals = getSplitTotals(details);

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Vendor Payment (Split)
          </span>
          <Badge variant="neutral">Split across {details.split_count ?? details.splits?.length ?? 0} vendors</Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
          <span className="text-caption text-subtext-color">Date</span>
          <span className="text-body-bold text-default-font">
            {formatDate(details?.date)}
          </span>
        </div>
        <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
          <span className="text-caption text-subtext-color">Reference Number</span>
          <span className="text-body-bold text-default-font">
            {details?.reference_number || "—"}
          </span>
        </div>
        <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
          <span className="text-caption text-subtext-color">Total Amount</span>
          <span className="text-body-bold text-default-font">
            {details?.total_amount != null
              ? formatCurrency(details.total_amount, transactionCurrencySymbol)
              : "—"}
          </span>
        </div>
      </div>

      {details?.splits && details.splits.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <span className="text-heading-4 font-heading-4 text-default-font">
            Split by Vendor ({details.split_count ?? details.splits.length})
          </span>
          <div className="flex w-full flex-col gap-3">
            {details.splits.map((split, index) => (
              <div
                key={`${split.vendor_id ?? index}-${index}`}
                className="flex w-full flex-col gap-2 rounded border border-neutral-border bg-white px-4 py-3"
              >
                <div className="flex w-full items-center justify-between border-b border-neutral-border pb-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {split.vendor_name || `Vendor ${index + 1}`}
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="text-body text-subtext-color">
                      {split.bills?.length ?? 0} bill(s)
                      {(split.advance_payment ?? 0) > 0 &&
                        ` · Advance: ${formatCurrency(split.advance_payment ?? 0, transactionCurrencySymbol)}`}
                    </span>
                    <span className="text-body-bold font-body-bold text-success-600">
                      {formatCurrency(split.amount, transactionCurrencySymbol)}
                    </span>
                  </div>
                </div>
                {split.bills && split.bills.length > 0 && (
                  <div className="overflow-x-auto rounded border border-neutral-border bg-neutral-50">
                    <table className="w-full min-w-[320px]">
                      <thead>
                        <tr className="border-b border-neutral-border">
                          <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                            Bill #
                          </th>
                          <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                            Total
                          </th>
                          <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                            Balance
                          </th>
                          <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                            Amount applied
                          </th>
                          <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {split.bills.map((bill, i) => (
                          <tr
                            key={i}
                            className="border-b border-neutral-border last:border-b-0"
                          >
                            <td className="px-2 py-1.5 text-body text-default-font">
                              {bill.bill_number ?? bill.bill_id ?? "—"}
                            </td>
                            <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                              {(bill.currency_symbol ?? transactionCurrencySymbol)}{" "}
                              {Number(bill.total ?? 0).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                              {(bill.currency_symbol ?? transactionCurrencySymbol)}{" "}
                              {Number(bill.balance ?? 0).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                              <div className="flex flex-col gap-0.5">
                                <span>
                                  {formatCurrency(
                                    Number(bill.amount_applied ?? 0),
                                    bill.currency_symbol ??
                                      transactionCurrencySymbol,
                                  )}
                                </span>
                                {Number(bill.adjustment_amount ?? 0) > 0 && (
                                  <span className="text-caption text-subtext-color">
                                    Adjustment:{" "}
                                    {formatCurrency(
                                      Number(bill.adjustment_amount ?? 0),
                                      bill.currency_symbol ??
                                        transactionCurrencySymbol,
                                    )}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-body text-subtext-color">
                              {bill.status ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="grid w-full grid-cols-1 gap-3 border-t border-neutral-border pt-3 md:grid-cols-3">
            <div className="flex flex-col rounded border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Amount Applied
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {formatCurrency(totals.amountApplied, transactionCurrencySymbol)}
              </span>
            </div>
            <div className="flex flex-col rounded border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Adjustment
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {formatCurrency(totals.adjustment, transactionCurrencySymbol)}
              </span>
            </div>
            <div className="flex flex-col rounded border border-dashed border-neutral-border px-4 py-3">
              <span className="text-caption text-subtext-color">
                Advance Payment
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {formatCurrency(totals.advancePayment, transactionCurrencySymbol)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SplitVendorViewContent;
